// Belge Dizaynı — AI destekli tasarım paneli (#138 Belge Dizayn Zenginleştirme).
// İki yetenek tek sohbet panelinde:
//  - Görselden rapor tasarımı: bir belge görseli/ekran görüntüsü yükle → LayoutJson üretilir.
//  - Sohbetli/iteratif tasarım: "üstte logo, sağda fatura no, altta kalem tablosu" gibi komutlarla
//    mevcut tasarımı tekrar tekrar düzenle.
// Üretilen layout, designer'ın setLayout'una verilir (geri-al/ileri-al ile geri alınabilir).

import { useState, useRef, useEffect } from 'react';
import { s, authHeaders } from '../listPageStyles';

const API = '/api/documenttemplates/AiDesignStream';

// Büyük base64 görselleri (logo vb.) modele göndermeden çıkar — token tasarrufu;
// model zaten src'yi boş bırakacak şekilde yönlendiriliyor.
function sanitizeLayout(layout) {
  if (!layout) return null;
  try {
    const clone = JSON.parse(JSON.stringify(layout));
    (clone.elements || []).forEach((el) => {
      if (el.type === 'image' && typeof el.src === 'string' && el.src.startsWith('data:')) el.src = '';
    });
    return clone;
  } catch {
    return layout;
  }
}

// --- Akan (henüz tamamlanmamış) JSON'dan canlı render için kısmi layout ayıklama ---
// Dengeli { } veya [ ] bölgesini döndürür (string/escape farkında); kapanmamışsa null.
function extractBalanced(text, start) {
  const open = text[start];
  const close = open === '{' ? '}' : ']';
  let depth = 0; let inStr = false; let esc = false;
  for (let i = start; i < text.length; i += 1) {
    const c = text[i];
    if (inStr) {
      if (esc) esc = false;
      else if (c === '\\') esc = true;
      else if (c === '"') inStr = false;
      continue;
    }
    if (c === '"') inStr = true;
    else if (c === open) depth += 1;
    else if (c === close) { depth -= 1; if (depth === 0) return text.slice(start, i + 1); }
  }
  return null;
}

// "key" sonrası değerin başladığı indeks (':' sonrası ilk boşluksuz karakter).
function valueStart(text, key, from = 0) {
  const k = `"${key}"`;
  const ki = text.indexOf(k, from);
  if (ki < 0) return -1;
  let i = text.indexOf(':', ki + k.length);
  if (i < 0) return -1;
  i += 1;
  while (i < text.length && /\s/.test(text[i])) i += 1;
  return i;
}

// Akan metinden o ana kadar TAMAMLANMIŞ page/elements/detailBand'i çıkarır.
function parsePartialLayout(text) {
  const out = {};
  const ps = valueStart(text, 'page');
  if (ps >= 0 && text[ps] === '{') {
    const s = extractBalanced(text, ps);
    if (s) { try { out.page = JSON.parse(s); } catch { /* henüz tam değil */ } }
  }
  const ds = valueStart(text, 'detailBand');
  if (ds >= 0 && text[ds] === '{') {
    const s = extractBalanced(text, ds);
    if (s) { try { out.detailBand = JSON.parse(s); } catch { /* henüz tam değil */ } }
  }
  const es = valueStart(text, 'elements');
  if (es >= 0 && text[es] === '[') {
    const els = [];
    let i = es + 1;
    while (i < text.length) {
      while (i < text.length && /[\s,]/.test(text[i])) i += 1;
      if (text[i] === ']' || text[i] === undefined) break;
      if (text[i] !== '{') break;
      const s = extractBalanced(text, i);
      if (!s) break; // son eleman henüz tamamlanmadı
      let obj = null;
      try { obj = JSON.parse(s); } catch { break; }
      if (obj && obj.id && obj.type) els.push(obj);
      i += s.length;
    }
    out.elements = els;
  }
  return out;
}

const QUICK_PROMPTS = [
  'Standart bir fatura düzeni oluştur (logo, müşteri bilgileri, kalem tablosu, toplamlar).',
  'Kalem tablosuna toplam (footer) satırı ekle.',
  'En alta tutarı yazıyla ve imza alanı ekle.',
  'Termal (72mm) yazıcı için sade tek sütunlu düzene çevir.',
];

export function AiDesignPanel({ open = true, docType, layout, onThinking, onLivePreview, onPlan, onClose, showToast }) {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: 'Merhaba! Belge tasarımında yardımcı olayım. Bir komut yazın (ör. *"standart fatura düzeni oluştur"*) '
        + 'veya bir belge görseli yükleyip ondan tasarım üretmemi isteyin.',
    },
  ]);
  const [input, setInput] = useState('');
  const [image, setImage] = useState(null); // { base64, mediaType, preview, name }
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [streamLen, setStreamLen] = useState(0);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  // Bekleme sırasında geçen süreyi say (donmuş değil, çalışıyor hissi).
  useEffect(() => {
    if (!loading) { setElapsed(0); return undefined; }
    const t0 = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(id);
  }, [loading]);

  const pickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || '');
      const comma = url.indexOf(',');
      const base64 = comma >= 0 ? url.slice(comma + 1) : url;
      const mediaType = (url.match(/^data:([^;]+);/) || [])[1] || file.type || 'image/png';
      setImage({ base64, mediaType, preview: url, name: file.name });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if ((!text && !image) || loading) return;

    const userMsg = { role: 'user', text: text || '(görsel yüklendi)', hasImage: !!image, preview: image?.preview };
    const history = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, text: m.text }));

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    const sentImage = image;
    setImage(null);
    setLoading(true);
    setStreamLen(0);
    onThinking?.(true);

    const ctrl = new AbortController();
    const timeoutId = setTimeout(() => ctrl.abort(), 180000); // 180s güvenlik kesintisi
    let done = null;   // { reply, layoutJson }
    let serverError = null;
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: authHeaders(),
        signal: ctrl.signal,
        body: JSON.stringify({
          docType,
          message: text,
          currentLayoutJson: JSON.stringify(sanitizeLayout(layout)),
          history,
          imageBase64: sentImage?.base64 || '',
          imageMediaType: sentImage?.mediaType || 'image/png',
        }),
      });
      if (!res.ok || !res.body) {
        let msg = 'AI tasarım isteği başarısız';
        try { const j = await res.json(); msg = j?.message || j?.Message || msg; } catch { /* yut */ }
        throw new Error(msg);
      }

      // SSE akışını oku: her "data: {json}" olayını ayrıştır; akan JSON'dan canlı render.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let acc = '';        // o ana kadar biriken model metni (kısmi JSON)
      let lastCount = -1;  // son render'da basılan eleman sayısı (gereksiz render'ı önle)
      let streaming = true;
      while (streaming) {
        const { value, done: rdDone } = await reader.read();
        if (rdDone) break;
        buf += decoder.decode(value, { stream: true });
        let sep;
        // eslint-disable-next-line no-cond-assign
        while ((sep = buf.indexOf('\n\n')) >= 0) {
          const evtRaw = buf.slice(0, sep);
          buf = buf.slice(sep + 2);
          const dataLine = evtRaw.split('\n').find((l) => l.startsWith('data:'));
          if (!dataLine) continue;
          let payload = null;
          try { payload = JSON.parse(dataLine.slice(5).trim()); } catch { payload = null; }
          if (!payload) continue;
          if (payload.type === 'delta') {
            acc += payload.text || '';
            setStreamLen(payload.length || acc.length);
            const partial = parsePartialLayout(acc);
            const cnt = partial.elements ? partial.elements.length : 0;
            // Yeni eleman tamamlandığında veya sayfa ilk geldiğinde tuvali güncelle (canlı çizim).
            if ((cnt !== lastCount && (cnt > 0 || partial.page)) || (partial.page && lastCount < 0)) {
              lastCount = cnt;
              onLivePreview?.(partial);
            }
          } else if (payload.type === 'done') {
            done = payload;
            streaming = false;
            break;
          } else if (payload.type === 'error') {
            serverError = payload.message || 'AI tasarım hatası';
            streaming = false;
            break;
          }
        }
      }

      if (serverError) throw new Error(serverError);

      const reply = done?.reply || 'Tamam.';
      setMessages((prev) => [...prev, { role: 'assistant', text: reply }]);

      if (done?.layoutJson) {
        let parsed = null;
        try { parsed = JSON.parse(done.layoutJson); } catch { parsed = null; }
        if (parsed && (Array.isArray(parsed.elements) || parsed.page)) {
          // Tuvale uygulamayı designer'a bırak — canlı "tasarlanıyor" animasyonuyla yerleştirir.
          onPlan?.(parsed);
        }
      }
    } catch (e) {
      const msg = e.name === 'AbortError'
        ? 'İstek zaman aşımına uğradı (sunucu yanıt vermedi). AI sağlayıcı/anahtar yapılandırmasını kontrol edin.'
        : (e.message || 'Bir hata oluştu.');
      setMessages((prev) => [...prev, { role: 'assistant', text: `⚠️ ${msg}`, error: true }]);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
      onThinking?.(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div style={{ ...st.drawer, display: open ? 'flex' : 'none' }}>
      <div style={st.head}>
        <span style={{ fontWeight: 800, fontSize: 14, color: 'var(--text-primary)' }}>✦ Rona AI Tasarım</span>
        <button style={st.closeBtn} onClick={onClose} title="Kapat">✕</button>
      </div>

      <div ref={scrollRef} style={st.scroll}>
        {messages.map((m, i) => (
          <div key={i} style={{ ...st.row, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={m.role === 'user' ? st.bubbleUser : (m.error ? st.bubbleErr : st.bubbleBot)}>
              {m.preview && <img src={m.preview} alt="" style={st.thumb} />}
              <span style={{ whiteSpace: 'pre-wrap' }}>{m.text}</span>
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ ...st.row, justifyContent: 'flex-start' }}>
            <div style={st.bubbleBot}><span style={st.dots}>✦ Rona AI tasarlıyor… {streamLen > 0 ? `${streamLen} karakter` : (elapsed > 0 ? `${elapsed}sn` : '')}</span></div>
          </div>
        )}
      </div>

      {messages.length <= 1 && (
        <div style={st.quickWrap}>
          {QUICK_PROMPTS.map((q) => (
            <button key={q} style={st.quick} disabled={loading} onClick={() => send(q)}>{q}</button>
          ))}
        </div>
      )}

      {image && (
        <div style={st.attach}>
          <img src={image.preview} alt="" style={st.attachThumb} />
          <span style={{ flex: 1, fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{image.name}</span>
          <button style={st.closeBtn} onClick={() => setImage(null)} title="Görseli kaldır">✕</button>
        </div>
      )}

      <div style={st.inputBar}>
        <button style={st.iconBtn} title="Belge görseli yükle" disabled={loading} onClick={() => fileRef.current?.click()}>📎</button>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickImage} />
        <textarea
          style={st.textarea}
          rows={2}
          placeholder="Komut yazın… (Enter = gönder, Shift+Enter = yeni satır)"
          value={input}
          disabled={loading}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button style={{ ...s.btnPrimary, padding: '8px 14px' }} disabled={loading || (!input.trim() && !image)} onClick={() => send()}>
          Gönder
        </button>
      </div>
    </div>
  );
}

const st = {
  // Flex akışında — sağ özellik panelinin SAĞINA yerleşir (üstüne binmez).
  drawer: {
    width: 360, maxWidth: '40vw', flexShrink: 0, alignSelf: 'stretch',
    background: 'var(--bg-card)', borderLeft: '1px solid var(--border-color)', boxShadow: 'var(--shadow-card)',
    flexDirection: 'column', minHeight: 0,
  },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: '1px solid var(--border-color)', background: 'var(--accent-soft)', flexShrink: 0 },
  closeBtn: { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, color: 'var(--text-secondary)', lineHeight: 1, padding: 4 },
  scroll: { flex: 1, minHeight: 0, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--page-bg)' },
  row: { display: 'flex' },
  bubbleBot: { maxWidth: '85%', background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: '12px 12px 12px 4px', padding: '8px 11px', fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.45 },
  bubbleUser: { maxWidth: '85%', background: 'var(--accent)', color: '#fff', borderRadius: '12px 12px 4px 12px', padding: '8px 11px', fontSize: 13, lineHeight: 1.45 },
  bubbleErr: { maxWidth: '85%', background: 'rgba(226,68,92,0.10)', border: '1px solid rgba(226,68,92,0.35)', color: 'var(--danger)', borderRadius: '12px 12px 12px 4px', padding: '8px 11px', fontSize: 13 },
  thumb: { display: 'block', maxWidth: '100%', maxHeight: 120, borderRadius: 6, marginBottom: 6 },
  dots: { color: 'var(--text-secondary)', fontStyle: 'italic' },
  quickWrap: { display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 12px', borderTop: '1px solid var(--border-color)', flexShrink: 0 },
  quick: { textAlign: 'left', border: '1px solid var(--border-color)', background: 'var(--input-bg)', borderRadius: 8, padding: '7px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--text-primary)' },
  attach: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderTop: '1px solid var(--border-color)', background: 'var(--bg-card)', flexShrink: 0 },
  attachThumb: { width: 36, height: 36, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border-color)' },
  inputBar: { display: 'flex', alignItems: 'flex-end', gap: 6, padding: 10, borderTop: '1px solid var(--border-color)', background: 'var(--bg-card)', flexShrink: 0 },
  iconBtn: { border: '1px solid var(--border-color)', background: 'var(--input-bg)', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', fontSize: 15, color: 'var(--text-primary)' },
  textarea: { flex: 1, resize: 'none', border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', lineHeight: 1.4, outline: 'none', background: 'var(--input-bg)', color: 'var(--text-primary)' },
};
